import { useCallback, useState } from 'react';
import { Package, WarningCircle } from 'shared/icons';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { BigAction, CodeLine } from './CollectorChrome';
import ScanInput from './ScanInput';
import { PlayScanError, PlayScanSuccess } from './collectorFeedback';
import {
    IsDropLocationRequiredError,
    PAUSE_REASONS,
    ParseServiceOrder,
    PauseServiceOrder,
} from './collectorServiceOrders';

/**
 * PAUSAR A OS — motivo obrigatório e, **carregando algo, o bipe de onde a carga
 * ficou** (§6.5).
 *
 * ── Por que o bipe da carga não é um campo opcional ─────────────────────────
 * Pausa é quase sempre almoço ou fim de turno: o operador vai embora. Se ele
 * larga o pallet num lugar e o sistema continua achando que a carga está na
 * posição de origem, quem vier depois procura no lugar errado — e o pallet
 * "some" sem ninguém ter feito nada errado. Por isso, quando há carga na mão, o
 * bipe do endereço é o ÚLTIMO passo e a pausa acontece com ele: não existe
 * botão "pausar mesmo assim".
 *
 * ── Duas fontes para "estou carregando" ─────────────────────────────────────
 * A tela sabe pelo próprio fluxo (o LPN já foi bipado e o destino ainda não).
 * O BACKEND sabe pelo item `EXECUTING` — e pode saber quando a tela não sabe
 * (coletor reaberto no meio de um item). Nesse caso ele recusa a pausa pedindo
 * o endereço, e isso não é erro do operador: o painel troca para o passo do
 * bipe e mostra a frase do servidor, que já diz qual LPN está na mão.
 */
const PauseServiceOrderPanel = ({ label, order, operator, carryingLpnCode = null, onCancel, onPaused }) => {
    const [reason, setReason] = useState(null);
    const [busy, setBusy] = useState(false);
    // Sobrescrito pelo 400 do backend quando ELE sabe de uma carga que a tela
    // não sabia. Nunca volta para false sozinho: carga na mão não desaparece.
    const [dropDemand, setDropDemand] = useState(carryingLpnCode ? { lpnCode: carryingLpnCode, message: null } : null);
    const [errorMessage, setErrorMessage] = useState(null);

    const submit = useCallback(
        async ({ dropLocationCode = null, scanMethod = null } = {}) => {
            if (!reason) {
                return;
            }
            setBusy(true);
            setErrorMessage(null);
            try {
                const payload = await PauseServiceOrder(order.id, {
                    reason,
                    operatorCode: operator?.employee_code || null,
                    carryingLpnCode: dropLocationCode ? dropDemand?.lpnCode || null : null,
                    dropLocationCode,
                    scanMethod,
                });
                PlayScanSuccess();
                onPaused(ParseServiceOrder(payload));
            } catch (error) {
                PlayScanError();
                if (IsDropLocationRequiredError(error)) {
                    setDropDemand({ lpnCode: dropDemand?.lpnCode || null, message: error.message });
                    return;
                }
                setErrorMessage(error?.message || label('wms.common.load_error'));
            } finally {
                setBusy(false);
            }
        },
        [dropDemand, label, onPaused, operator, order.id, reason],
    );

    const needsDrop = Boolean(dropDemand);
    const showDropStep = needsDrop && Boolean(reason);

    return (
        <div className="flex min-h-[70vh] flex-col gap-4">
            <div className="flex flex-1 flex-col justify-center gap-4">
                <h2 className="text-center text-2xl font-black text-black dark:text-white">
                    {label('wms.pause.title')}
                </h2>
                <CodeLine>{order?.code}</CodeLine>

                <div className="space-y-2">
                    <p className="text-sm font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.pause.reason')}
                    </p>
                    {PAUSE_REASONS.map((option) => {
                        const selected = reason === option;
                        return (
                            <button
                                key={option}
                                type="button"
                                onClick={() => setReason(option)}
                                aria-pressed={selected}
                                className={`w-full rounded-xl border-2 px-4 py-4 text-left text-lg font-bold transition-colors ${
                                    selected
                                        ? 'border-black bg-mono-100 text-black dark:border-white dark:bg-mono-800 dark:text-white'
                                        : 'border-mono-300 bg-white text-mono-700 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-300'
                                }`}
                            >
                                {label(`wms.pause.reason.${option}`)}
                            </button>
                        );
                    })}
                </div>

                {showDropStep ? (
                    <div className="space-y-3 rounded-2xl border-2 border-warning-400 bg-warning-soft p-4 dark:border-warning-700 dark:bg-warning-900/30">
                        <div className="flex items-center justify-center gap-2 text-warning-text dark:text-warning-200">
                            <Package size={24} className="shrink-0" />
                            <p className="text-lg font-black">{label('wms.collector.carrying_title')}</p>
                        </div>
                        {dropDemand.lpnCode ? (
                            <Mono className="block break-all text-center text-xl font-black text-warning-text dark:text-warning-200">
                                {dropDemand.lpnCode}
                            </Mono>
                        ) : null}
                        <p className="text-center text-2xl font-black leading-tight text-warning-text dark:text-warning-100">
                            {label('wms.pause.drop_location')}
                        </p>
                        <p className="text-center text-sm text-warning-text dark:text-warning-300">
                            {dropDemand.message || label('wms.pause.drop_location_help')}
                        </p>
                        <ScanInput
                            label={label}
                            onScan={(code, scanMethod) => submit({ dropLocationCode: code, scanMethod })}
                            disabled={busy}
                            focusKey={`pause-drop-${reason}`}
                            manualPlaceholder="STG-R02-00-00"
                        />
                    </div>
                ) : null}

                {errorMessage ? (
                    <div
                        role="alert"
                        className="flex items-start gap-2 rounded-xl bg-danger-soft p-3 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                    >
                        <WarningCircle size={18} className="mt-0.5 shrink-0" />
                        <span>{errorMessage}</span>
                    </div>
                ) : null}
            </div>

            <div className="sticky bottom-0 space-y-2 pt-2">
                {/* Sem carga na mão, pausar é um toque só. Com carga, o botão
                    NÃO existe: a pausa sai do bipe do endereço — é o jeito de a
                    regra "bipe onde a carga ficou" não ter porta dos fundos. */}
                {!needsDrop ? (
                    <BigAction onClick={() => submit()} loading={busy} disabled={!reason}>
                        {label('wms.collector.pause_confirm')}
                    </BigAction>
                ) : null}
                <Button variant="ghost" size="lg" className="w-full" onClick={onCancel} disabled={busy}>
                    {label('wms.common.cancel')}
                </Button>
            </div>
        </div>
    );
};

export default PauseServiceOrderPanel;
